import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import type { UnitLevelCoverages } from "@covertree/new-business-graphql";
import { getDefaultValuesForCustomization } from "@covertree/new-business-forms";
import type { UnitWithCoverages } from "@covertree/new-business-redux";

import type { EndorsementFormValues } from "@layouts/EndorsementLayout/types";

import { NO_POLICY_LEVEL_COVERAGES } from "./constants";

const getDefaults = (coverages: UnitLevelCoverages) => {
  const unit = {
    exposureLocator: "",
    unitLevelCoverages: coverages,
  } as UnitWithCoverages;

  return getDefaultValuesForCustomization(
    "",
    NO_POLICY_LEVEL_COVERAGES,
    [unit],
    [],
    [unit]
  ).units[0].unitLevelCoverages;
};

/**
 * Fills missing base values; `reset` keeps them out of dirtyFields.
 * Returns `true` once seeded: sliders must mount with a value,
 * or MUI warns about uncontrolled -> controlled.
 */
export const useSeedBaseDefaults = (base?: UnitLevelCoverages) => {
  const { getValues, reset } = useFormContext<EndorsementFormValues>();
  const [isSeeded, setIsSeeded] = useState(false);

  useEffect(() => {
    if (!base) return;

    const defaults = getDefaults(base);
    const values = getValues();
    const current = values.units?.[0]?.unitLevelCoverages ?? {};

    if (!Object.keys(defaults).every((key) => key in current)) {
      reset(
        {
          ...values,
          units: [
            {
              ...values.units?.[0],
              unitLevelCoverages: { ...defaults, ...current },
            },
          ],
        },
        { keepDirtyValues: true }
      );
    }

    setIsSeeded(true);
  }, [base, getValues, reset]);

  return isSeeded;
};
